import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Dialog, Flex, Select, Text, TextField } from '@radix-ui/themes'
import { PlusIcon } from '@radix-ui/react-icons'
import { Ops } from '@willie/schemas'
import { FieldShell } from '~/components/FieldShell'
import { useTRPC } from '~/utils/trpc'
import { useMeterMakerCatalog, withSelectedMakers } from '../useMeterMakers'

const NONE = '__none__'

interface NewMeterMakerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (slug: string) => void
}

const NewMeterMakerDialog = ({ open, onOpenChange, onCreated }: NewMeterMakerDialogProps) => {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [label, setLabel] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)

  const createMutation = useMutation(
    trpc.ops.meterMakers.create.mutationOptions({
      onSuccess: () => queryClient.invalidateQueries({ queryKey: trpc.ops.meterMakers.list.queryKey() })
    })
  )

  const slug = Ops.MeterMakers.toMeterMakerSlug(label)

  // Every close routes through here: reopening never fires Radix's onOpenChange, so a
  // mutation error left behind would surface in the next, empty dialog.
  const closeDialog = () => {
    setLabel('')
    setValidationError(null)
    createMutation.reset()
    onOpenChange(false)
  }

  const createMaker = async () => {
    if (createMutation.isPending) return
    const parsed = Ops.MeterMakers.meterMakerInputSchema.safeParse({ slug, label })
    if (!parsed.success) {
      setValidationError(parsed.error.issues[0]?.message ?? 'Check the maker name.')
      return
    }
    setValidationError(null)
    try {
      const created = await createMutation.mutateAsync(parsed.data)
      onCreated(created.slug)
      closeDialog()
    } catch {
      // Keep the dialog open; the mutation error is displayed below.
    }
  }

  const errorMessage = validationError ?? createMutation.error?.message ?? null

  return (
    <Dialog.Root open={open} onOpenChange={(next) => (next ? onOpenChange(true) : closeDialog())}>
      <Dialog.Content maxWidth="420px">
        <Dialog.Title>Add meter maker</Dialog.Title>
        <Dialog.Description size="2" color="gray" mb="4">
          The maker becomes available everywhere in ops, for every installation.
        </Dialog.Description>
        <TextField.Root
          autoFocus
          value={label}
          placeholder="e.g. Fuji Electric"
          onChange={(event) => setLabel(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && slug) {
              event.preventDefault()
              void createMaker()
            }
          }}
        />
        {slug ? (
          <Text size="1" color="gray" className="block mt-1 font-mono">
            {slug}
          </Text>
        ) : null}
        {errorMessage ? (
          <Text size="1" color="red" className="block mt-2">
            {errorMessage}
          </Text>
        ) : null}
        <Flex justify="end" gap="2" mt="4">
          <Dialog.Close>
            <Button type="button" variant="soft" color="gray">
              Cancel
            </Button>
          </Dialog.Close>
          <Button type="button" onClick={createMaker} disabled={!slug || createMutation.isPending}>
            Add maker
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}

interface MeterMakerPickerProps {
  value: string | null
  onChange: (slug: string | null) => void
}

export const MeterMakerPicker = ({ value, onChange }: MeterMakerPickerProps) => {
  const catalog = useMeterMakerCatalog()
  const [dialogOpen, setDialogOpen] = useState(false)

  return (
    <>
      <FieldShell
        label="Meter maker"
        action={
          <Button type="button" size="1" variant="ghost" onClick={() => setDialogOpen(true)}>
            <PlusIcon />
            New maker
          </Button>
        }
        hint={
          catalog.isError ? (
            <Text size="1" color="red">
              Could not load meter makers.
            </Text>
          ) : null
        }
      >
        <Select.Root
          value={value ?? NONE}
          disabled={catalog.isLoading}
          onValueChange={(next) => onChange(next === NONE ? null : next)}
        >
          <Select.Trigger className="w-full" placeholder="Select a maker" />
          <Select.Content>
            <Select.Item value={NONE}>—</Select.Item>
            {withSelectedMakers(catalog, value ? [value] : []).map((maker) => (
              <Select.Item key={maker.slug} value={maker.slug}>
                {maker.label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
      </FieldShell>

      <NewMeterMakerDialog open={dialogOpen} onOpenChange={setDialogOpen} onCreated={onChange} />
    </>
  )
}
